import { type ReactNode } from "react";
import type { ERPProviderKey } from "@forgepoint/shared";
import { LockedSettingBadge } from "@/components/settings/LockedSettingBadge";

interface SettingsRowProps {
  /** Label text displayed on the left side of the row */
  label: string;
  /** Optional description text shown below the label */
  description?: string;
  /** Control element rendered on the right side */
  children: ReactNode;
  /** Optional content rendered below the main row when expanded */
  expandedContent?: ReactNode;
  /**
   * Optional DOM id used as a scroll/highlight anchor by settings search.
   * `scroll-mt` keeps the row clear of the sticky settings header when scrolled to.
   */
  id?: string;
  /** ERPs forcing this setting; shows a "Required by <ERP>" badge when present */
  lockedBy?: ERPProviderKey[];
}

/**
 * A horizontal settings row with label/description on the left and control on the right.
 * Used in the agent settings page for a consistent vertical list layout.
 * Shows a "Required by <ERP>" badge when ERPs lock the setting; panels disable the control.
 * @param props - SettingsRowProps
 * @returns Settings row element
 */
export function SettingsRow({
  label,
  description,
  children,
  expandedContent,
  id,
  lockedBy,
}: SettingsRowProps) {
  return (
    <div id={id} className="px-4 scroll-mt-24">
      <div className="flex items-center justify-between gap-4 py-3.5">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <p className="text-sm font-medium leading-tight">{label}</p>
            {lockedBy && lockedBy.length > 0 && (
              <LockedSettingBadge providers={lockedBy} />
            )}
          </div>
          {description && (
            <p className="text-xs text-muted-foreground mt-0.5 max-w-md leading-snug">
              {description}
            </p>
          )}
        </div>
        <div className="shrink-0 flex items-center gap-2">{children}</div>
      </div>
      {expandedContent && <div className="pb-3.5">{expandedContent}</div>}
    </div>
  );
}
